import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  Patient,
  CreatePatientData,
  fetchPatientsByPhoneComplete,
  fetchRegistryReviewConfig,
  updatePatient,
} from "@/services/patient-service";
import {
  AppointmentData,
  fetchAppointments,
} from "@/services/appointment-service";
import { fetchInsurances, Insurance } from "@/services/insurance-service";
import { CreateEditPatientDialog } from "@/components/patients/CreateEditPatientDialog";
import { statusLabels, statusColors } from "@/utils/appointmentStatus";
import { formatCPF, formatPhone } from "@/utils/formatters";
import {
  Calendar,
  ChevronDown,
  ChevronUp,
  Edit,
  Loader2,
  Phone,
  Users,
} from "lucide-react";

interface PatientsByPhoneDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cellphone: string | undefined;
  clinicId: string | undefined;
}

const APPOINTMENT_WINDOW_DAYS = 30;

function toIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function formatDateBR(iso?: string | null): string {
  if (!iso) return "—";
  const d = iso.slice(0, 10);
  return d.split("-").reverse().join("/");
}

function formatDateTimeBR(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const pad = (n: number) => n.toString().padStart(2, "0");
  return (
    `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} às ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
}

export function PatientsByPhoneDialog({
  open,
  onOpenChange,
  cellphone,
  clinicId,
}: PatientsByPhoneDialogProps) {
  const { toast } = useToast();

  const [patients, setPatients] = useState<Patient[]>([]);
  const [loadingPatients, setLoadingPatients] = useState(false);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [appointmentsByPatient, setAppointmentsByPatient] = useState<
    Record<string, AppointmentData[]>
  >({});
  const [loadingAppointmentsFor, setLoadingAppointmentsFor] =
    useState<string | null>(null);

  const [reviewDaysInterval, setReviewDaysInterval] = useState<number | null>(
    null,
  );

  const [insuranceMap, setInsuranceMap] = useState<Record<string, string>>({});

  const [editingPatient, setEditingPatient] = useState<Patient | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const { startDateIso, endDateIso } = useMemo(() => {
    const today = new Date();
    const start = new Date(today);
    start.setDate(today.getDate() - APPOINTMENT_WINDOW_DAYS);
    const end = new Date(today);
    end.setDate(today.getDate() + APPOINTMENT_WINDOW_DAYS);
    return { startDateIso: toIsoDate(start), endDateIso: toIsoDate(end) };
  }, []);

  // Carrega config global de revisão (1x quando abrir, se ainda não tem).
  useEffect(() => {
    if (!open || reviewDaysInterval !== null) return;
    fetchRegistryReviewConfig()
      .then((cfg) => setReviewDaysInterval(cfg.days_interval))
      .catch(() => setReviewDaysInterval(60));
  }, [open, reviewDaysInterval]);

  // Carrega map id→nome dos convênios (1x quando abrir).
  useEffect(() => {
    if (!open || Object.keys(insuranceMap).length > 0) return;
    fetchInsurances(true, false, 1, 1000)
      .then((list: Insurance[]) => {
        const map: Record<string, string> = {};
        for (const i of list) map[i.id] = i.name;
        setInsuranceMap(map);
      })
      .catch(() => {
        /* convênio fica como "—" se falhar */
      });
  }, [open, insuranceMap]);

  // Carrega pacientes do telefone toda vez que abrir / mudar telefone ou clínica.
  useEffect(() => {
    if (!open || !cellphone || !clinicId) {
      setPatients([]);
      setExpandedId(null);
      setAppointmentsByPatient({});
      return;
    }
    setLoadingPatients(true);
    fetchPatientsByPhoneComplete(cellphone, clinicId)
      .then((list) => {
        setPatients(list);
        // se só 1 paciente, expande direto
        if (list.length === 1) setExpandedId(list[0].id);
      })
      .catch((err) => {
        toast({
          title: "Erro ao buscar pacientes",
          description: err instanceof Error ? err.message : undefined,
          variant: "destructive",
        });
      })
      .finally(() => setLoadingPatients(false));
  }, [open, cellphone, clinicId, toast]);

  // Carrega atendimentos do paciente expandido (cacheia em memória).
  useEffect(() => {
    if (!expandedId || !clinicId) return;
    if (appointmentsByPatient[expandedId] !== undefined) return;

    setLoadingAppointmentsFor(expandedId);
    fetchAppointments(
      false,
      false,
      clinicId,
      expandedId,
      undefined,
      undefined,
      undefined,
      startDateIso,
      endDateIso,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      1,
      100,
    )
      .then((rows) => {
        const sorted = [...rows].sort((a, b) => {
          const da = `${a.slot_date}T${a.slot_time || "00:00:00"}`;
          const db = `${b.slot_date}T${b.slot_time || "00:00:00"}`;
          return db.localeCompare(da); // mais recente primeiro
        });
        setAppointmentsByPatient((prev) => ({ ...prev, [expandedId]: sorted }));
      })
      .catch((err) => {
        toast({
          title: "Erro ao buscar atendimentos",
          description: err instanceof Error ? err.message : undefined,
          variant: "destructive",
        });
        setAppointmentsByPatient((prev) => ({ ...prev, [expandedId]: [] }));
      })
      .finally(() => setLoadingAppointmentsFor(null));
  }, [expandedId, clinicId, startDateIso, endDateIso, appointmentsByPatient, toast]);

  const reviewBadge = (last?: string | null) => {
    if (reviewDaysInterval === null) return null;
    if (!last) {
      return (
        <span className="inline-flex items-center rounded-full border border-red-300 bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-800">
          Cadastro vencido
        </span>
      );
    }
    const reviewedAt = new Date(last).getTime();
    if (Number.isNaN(reviewedAt)) return null;
    const expiresAt = reviewedAt + reviewDaysInterval * 24 * 60 * 60 * 1000;
    const daysRemaining = Math.ceil(
      (expiresAt - Date.now()) / (24 * 60 * 60 * 1000),
    );
    if (daysRemaining <= 0) {
      return (
        <span className="inline-flex items-center rounded-full border border-red-300 bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-800">
          Cadastro vencido
        </span>
      );
    }
    if (daysRemaining <= 10) {
      return (
        <span className="inline-flex items-center rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
          Expira em {daysRemaining}d
        </span>
      );
    }
    return null;
  };

  const handleEdit = (patient: Patient) => {
    setEditingPatient(patient);
    setEditDialogOpen(true);
  };

  const handleSavePatient = async (
    data: CreatePatientData,
    patientId?: string,
  ) => {
    if (!patientId) return;
    try {
      await updatePatient(patientId, data);
      // atualiza o paciente na lista local sem refetch
      setPatients((prev) =>
        prev.map((p) =>
          p.id === patientId
            ? {
                ...p,
                ...data,
                last_registry_review_at: new Date().toISOString(),
              }
            : p,
        ),
      );
      setEditDialogOpen(false);
      toast({ title: "Paciente atualizado!" });
    } catch (error) {
      toast({
        title: "Erro ao atualizar paciente",
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive",
      });
      throw error; // mantém o CreateEditPatientDialog aberto
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="border-b pb-2">
            <DialogTitle className="text-sm font-semibold uppercase tracking-wide flex items-center gap-2">
              <Users className="h-4 w-4" />
              Pacientes desse telefone
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Phone className="h-3 w-3" />
              <span className="tabular-nums">
                {cellphone ? formatPhone(cellphone) || cellphone : "—"}
              </span>
              {!loadingPatients && (
                <span className="ml-auto">
                  {patients.length} paciente{patients.length === 1 ? "" : "s"}
                </span>
              )}
            </div>

            {loadingPatients ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            ) : patients.length === 0 ? (
              <div className="rounded-lg border border-dashed p-6 text-center text-xs text-muted-foreground">
                Nenhum paciente cadastrado com esse telefone.
              </div>
            ) : (
              <div className="space-y-2">
                {patients.map((patient) => {
                  const isExpanded = expandedId === patient.id;
                  const appts = appointmentsByPatient[patient.id];
                  const isLoadingAppts = loadingAppointmentsFor === patient.id;

                  return (
                    <div
                      key={patient.id}
                      className="rounded-lg border border-border bg-card"
                    >
                      <button
                        type="button"
                        className="w-full flex items-center gap-3 p-3 text-left hover:bg-muted/30 transition"
                        onClick={() =>
                          setExpandedId(isExpanded ? null : patient.id)
                        }
                      >
                        <div className="h-8 w-8 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-primary font-display font-semibold text-xs">
                          {patient.name?.[0]?.toUpperCase() || "?"}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium truncate">
                              {patient.name}
                            </span>
                            {reviewBadge(patient.last_registry_review_at)}
                          </div>
                          <div className="text-[11px] text-muted-foreground flex gap-3 flex-wrap mt-0.5">
                            <span>CPF {formatCPF(patient.cpf) || "—"}</span>
                            <span>Nasc {formatDateBR(patient.birth_date)}</span>
                          </div>
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        )}
                      </button>

                      {isExpanded && (
                        <div className="border-t border-border p-3 space-y-3">
                          {/* Bloco dados + botão editar */}
                          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                            <div className="space-y-0.5">
                              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                                Convênio
                              </p>
                              <p className="text-xs truncate">
                                {patient.dental_insurance_id
                                  ? insuranceMap[patient.dental_insurance_id] || "—"
                                  : "Particular"}
                              </p>
                            </div>
                            <div className="space-y-0.5">
                              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                                Carteirinha
                              </p>
                              <p className="text-xs">
                                {patient.card_number || "—"}
                              </p>
                            </div>
                            <div className="space-y-0.5 col-span-2">
                              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                                Última atualização
                              </p>
                              <p className="text-xs tabular-nums">
                                {patient.last_registry_review_at
                                  ? formatDateTimeBR(patient.last_registry_review_at)
                                  : "Nunca"}
                              </p>
                            </div>
                          </div>

                          <div className="flex justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              className="btn-header"
                              onClick={() => handleEdit(patient)}
                            >
                              <Edit className="h-3 w-3 mr-1.5" />
                              Editar paciente
                            </Button>
                          </div>

                          {/* Timeline de atendimentos */}
                          <div className="space-y-1.5">
                            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
                              <Calendar className="h-3 w-3" />
                              Atendimentos (±{APPOINTMENT_WINDOW_DAYS} dias)
                            </div>
                            {isLoadingAppts ? (
                              <div className="flex items-center justify-center py-4">
                                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                              </div>
                            ) : !appts || appts.length === 0 ? (
                              <div className="rounded border border-dashed p-3 text-center text-[11px] text-muted-foreground">
                                Nenhum atendimento nessa janela.
                              </div>
                            ) : (
                              <ul className="space-y-1">
                                {appts.map((a) => (
                                  <li
                                    key={a.id}
                                    className="flex items-center gap-3 p-2 rounded border border-border text-xs"
                                  >
                                    <span className="tabular-nums font-medium">
                                      {formatDateBR(a.slot_date)}{" "}
                                      <span className="text-muted-foreground">
                                        {a.slot_time?.slice(0, 5) || ""} hs
                                      </span>
                                    </span>
                                    <span
                                      className={`px-1.5 py-0.5 rounded border text-[10px] font-medium uppercase tracking-wide ${statusColors[a.appointment_status] || "border-border"}`}
                                    >
                                      {statusLabels[a.appointment_status] ||
                                        a.appointment_status}
                                    </span>
                                    <span className="text-muted-foreground truncate">
                                      {a.dental_specialty_name || "—"}
                                    </span>
                                    <span className="text-muted-foreground truncate">
                                      {a.dentist_name || "—"}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <CreateEditPatientDialog
        patient={editingPatient}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onSave={handleSavePatient}
      />
    </>
  );
}
